import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { ApiService } from '../../services/api.service';
import { HttpClient } from '@angular/common/http';
import { DateService } from 'src/app/services/date.service';

interface FileRef {
  title: string;
  contentType: string;
  distributorID: string;
  fileName: string;
  issuerID: string[];
  requestID?: string[];
  isForAll: boolean;
  id: string;
  fileExt?: string;
  createdOn?: Date;
  updatedOn?: Date;
  userTags?: string[];
  partition?: number;
}

@Injectable({
  providedIn: 'root',
})
export class FileManagementService {
  constructor(private apiService: ApiService, private httpClient: HttpClient, private dateService: DateService) {}

  async uploadRef(fileRef: FileRef, id: string) {
    return await this.apiService.post<any, any>(`ddq/upload-ref/${id}`, fileRef).toPromise();
  }

  // this one differs from the one above, please keep both!
  updateRef(fileRef: FileRef, id: string): Observable<any> {
    return this.apiService.post<any, any>(`ddq/upload-ref/${id}`, fileRef);
  }

  uploadFile(filename: string, fileBuffer: any, contentType: string, id: string): any {
    return this.apiService.post<any, any>(`ddq/upload/${id}_${filename}`, { fileBuffer, contentType });
  }

  uploadFilePartition(filename: string, fileBuffer: any, contentType: string, id: string, partitionNumber: number) {
    return this.apiService.post<any, any>(`ddq/upload/${id}_${filename}.part${partitionNumber}`, {
      fileBuffer,
      contentType,
    });
  }

  uploadLog(auditLog: any, id: string): any {
    this.apiService.post<any, any>(`ddq/upload-log/${id}`, auditLog).subscribe();
  }

  listFileMapping(): Observable<any[]> {
    return this.apiService.get<any[]>(`ddq/file-map`);
  }

  listFileMappingByIssuer(issuerID): Observable<any[]> {
    return this.apiService.get<any[]>(`ddq/file-map/${issuerID}`);
  }

  listFileRefsWithIDs(distributorID: string, issuerID: string, requestID: string): Observable<any[]> {
    return this.apiService.get<FileRef[]>(`ddq/file-list/${distributorID}/${issuerID}/${requestID}`);
  }

  // only for internal DC user
  listAllfiles(): Observable<any[]> {
    return this.apiService.get<FileRef[]>(`ddq/file-list`);
  }

  // old file list in s3 keep for historic reasons
  listAllfilesOld(): Observable<any[]> {
    return this.apiService.get<FileRef[]>(`ddq/file-list-old`);
  }

  listAuditLogs(filter): Observable<any[]> {
    const date = new Date();
    const currentDate = date.toISOString().slice(0, 10);
    date.setDate(date.getDate() - 7);
    const monthPriorDate = date.toISOString().slice(0, 10);

    const from = filter?.dateRange?.from
      ? `${filter.dateRange.from.year}-${this.dateService.toTwoDigits(
          filter.dateRange.from.month
        )}-${this.dateService.toTwoDigits(filter.dateRange.from.day)}`
      : monthPriorDate;

    const to = filter?.dateRange?.to
      ? `${filter.dateRange.to.year}-${this.dateService.toTwoDigits(
          filter.dateRange.to.month
        )}-${this.dateService.toTwoDigits(filter.dateRange.to.day)}`
      : currentDate;

    const action = filter?.action || 'all';

    return this.apiService.get<any[]>(`ddq/audit/${from}/${to}/${action}`);
  }

  listAuditKeys(): Observable<any[]> {
    return this.apiService.get<any[]>(`ddq/audit/list`);
  }

  getAuditLogByKey(key: string): Observable<any> {
    return this.apiService.get<any[]>(`ddq/audit/${key}`);
  }

  // use this in internal DC screen
  listFileRefsById(distributorID: string): Observable<any[]> {
    return this.apiService.get<FileRef[]>(`ddq/file-list/${distributorID}`);
  }

  async getFile(id: string) {
    const ref = await this.getRef(id).toPromise();
    if (ref.partition) {
      let result = { name: '', key: '', data: '' };
      for (let i = 0; i < ref.partition; i++) {
        const fileSegment = await this.apiService.get<any>(`ddq/get-file/${id}/partition/${i + 1}`).toPromise();
        result.data += fileSegment.data;
        result.name = fileSegment.name;
        result.key = fileSegment.key;
        console.log(`partition ${i + 1} downloaded ...`);
      }
      return result;
    } else {
      return this.apiService.get<any>(`ddq/get-file/${id}`).toPromise();
    }
  }

  getRef(id: string) {
    return this.apiService.get<any>(`ddq/file-ref/${id}`);
  }

  deleteFileRef(id: string) {
    return this.apiService.delete<any>(`ddq/file-ref/${id}`);
  }

  deleteFile(id: string, name: string) {
    return this.apiService.delete<any>(`ddq/file/${id}/${name}`);
  }

  getBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }

  arrayBufferToBase64(buffer) {
    var binary = '';
    var bytes = new Uint8Array(buffer);
    var len = bytes.byteLength;
    for (var i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  convertBase64ToBlob(base64Image: string) {
    // Split into two parts
    const parts = base64Image.split(';base64,');
    // Hold the content type
    const imageType = parts[0].split(':')[1];
    // Decode Base64 string
    const decodedData = window.atob(parts[1]);
    // Create UNIT8ARRAY of size same as row data length
    const uInt8Array = new Uint8Array(decodedData.length);
    // Insert all character code into uInt8Array
    for (let i = 0; i < decodedData.length; ++i) {
      uInt8Array[i] = decodedData.charCodeAt(i);
    }
    // Return BLOB image after conversion
    return new Blob([uInt8Array], { type: imageType });
  }
}
